import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { useAppSelector } from '../../../store/store';
import { selectUser } from '../../auth/authSlice';
import type {
  EnhancedInventoryItem,
  InventoryItemResponse,
} from '../../../api/types';
import {
  buildClusterHTML,
  buildMarkerHTML,
  deriveMarkerState,
  ensureMarkerStyles,
  MARKER_SIZE,
  MARKER_TAIL,
  type AnyItem,
} from './MapMarkerIcon';
import { MapControls } from './MapControls';

// Web-only modules — dynamic-require so this file stays safe to
// import on native RN.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let RL: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let L: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let MarkerClusterGroup: any = null;
if (Platform.OS === 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    RL = require('react-leaflet');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    L = require('leaflet');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require('leaflet/dist/leaflet.css');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require('leaflet.markercluster/dist/MarkerCluster.css');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require('leaflet.markercluster/dist/MarkerCluster.Default.css');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require('leaflet.markercluster');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    MarkerClusterGroup = require('react-leaflet-cluster').default;
  } catch {
    /* leaflet stack not available — fall through to fallback */
  }
}

/**
 * LocationMapView — Stage 2A of the Memory-Map work.
 *
 * Apple-Photos-style full-bleed map with:
 *   - Thumbnail markers (item's primary_photo_url) with custody-state
 *     borders (owned / lent-out / borrowed / overdue)
 *   - Clustering (Leaflet.markercluster) — cluster bubbles reuse the
 *     same thumbnail shape with a count badge
 *   - Floating control stack: zoom +/−, layer toggle, locate me, fit
 *   - User-location blue dot (one-shot on locate-me tap)
 *   - Style toggle: Standard OSM ↔ Esri WorldImagery satellite
 *   - Calgary fallback center when no items have GPS
 *
 * Stage 2B (after backend#140 lands) will add the two-pin "memory
 * map" feature — every item gets a faded ghost pin at scan_lat
 * AND a solid current pin at current_location_lat with a dashed
 * connecting line.
 *
 * Web-only — native RN falls back to a friendly message.
 */

interface LocationMapViewProps {
  items: AnyItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onItemPress: (item: any) => void;
}

const CALGARY_FALLBACK: [number, number] = [51.0447, -114.0719];
const DEFAULT_ZOOM = 12;

const escapeAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

const TILE_LAYERS = {
  standard: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    maxZoom: 19,
  },
} as const;

interface PlottableItem {
  item: AnyItem;
  /** Primary pin location — current_location if set, else scan. */
  lat: number;
  lng: number;
  /** When set, this item has BOTH a current location AND a scan
   *  location, and they differ. The map renders a faded ghost
   *  marker at scan + a dashed line connecting them. */
  scanGhost: { lat: number; lng: number } | null;
}

export const LocationMapView: React.FC<LocationMapViewProps> = ({
  items,
  onItemPress,
}) => {
  const currentUser = useAppSelector(selectUser);
  const currentUserId = currentUser?.id ?? null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  const [styleId, setStyleId] = useState<'standard' | 'satellite'>('standard');
  const [locating, setLocating] = useState(false);
  const [userPos, setUserPos] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (Platform.OS === 'web') ensureMarkerStyles();
  }, []);

  // Ref used by the auto-fit-on-data-ready effect below. Declared up
  // here at hook-rules order; the effect itself lives below the
  // `plottable` useMemo where it can reference its result.
  const lastFitCountRef = useRef<number>(-1);

  // Filter to items with usable GPS coords. Only render markers for
  // items the current user owns (privacy guard from PR #108 — never
  // expose another user's raw coords).
  //
  // Per-item pin resolution (Memory Map):
  //   - If `current_location_lat/lng` is set → primary pin there
  //     (solid thumbnail). If `scan_lat/lng` ALSO set and differs,
  //     a faded ghost marker plots at the scan location + a dashed
  //     polyline connects them.
  //   - Else fall back to `scan_lat/lng` alone (single solid pin).
  //   - Coords match (4dp rounded) → no ghost, single pin.
  const plottable = useMemo<PlottableItem[]>(() => {
    const out: PlottableItem[] = [];
    for (const it of items) {
      const ownerId =
        (it as any).user_id ?? (it as any).owner_user_id;
      if (currentUserId && ownerId && ownerId !== currentUserId) continue;
      const scanLat = (it as any).scan_lat;
      const scanLng = (it as any).scan_lng;
      const curLat = (it as any).current_location_lat;
      const curLng = (it as any).current_location_lng;
      const validScan =
        typeof scanLat === 'number'
        && typeof scanLng === 'number'
        && Number.isFinite(scanLat)
        && Number.isFinite(scanLng);
      const validCur =
        typeof curLat === 'number'
        && typeof curLng === 'number'
        && Number.isFinite(curLat)
        && Number.isFinite(curLng);
      if (!validScan && !validCur) continue;
      if (validCur) {
        const ghost =
          validScan
          && (Math.abs(curLat - scanLat) > 0.00005
            || Math.abs(curLng - scanLng) > 0.00005)
            ? { lat: scanLat, lng: scanLng }
            : null;
        out.push({ item: it, lat: curLat, lng: curLng, scanGhost: ghost });
      } else {
        out.push({
          item: it,
          lat: scanLat,
          lng: scanLng,
          scanGhost: null,
        });
      }
    }
    return out;
  }, [items, currentUserId]);

  const { center, zoom } = useMemo(() => {
    if (plottable.length === 0) {
      return { center: CALGARY_FALLBACK, zoom: DEFAULT_ZOOM };
    }
    if (plottable.length === 1) {
      return {
        center: [plottable[0].lat, plottable[0].lng] as [number, number],
        zoom: 14,
      };
    }
    const lats = plottable.map((p) => p.lat);
    const lngs = plottable.map((p) => p.lng);
    const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    return { center: [midLat, midLng] as [number, number], zoom: 11 };
  }, [plottable]);

  // Auto-fit the map to all plottable markers on first load. Without
  // this, the default `center + zoom=11` above picks the geometric
  // midpoint of all coords — which can land in the middle of the
  // ocean if a user has items in two different cities (e.g. Calgary +
  // Ahmedabad). Tracks `plottable.length` so it only fires when the
  // set changes size, not on every re-render. Apple Photos' Memory
  // Map uses the same one-shot fit-on-data-ready pattern.
  useEffect(() => {
    if (!mapRef.current || !L) return;
    if (plottable.length === 0) return;
    if (plottable.length === lastFitCountRef.current) return;
    lastFitCountRef.current = plottable.length;
    if (plottable.length === 1) {
      mapRef.current.setView(
        [plottable[0].lat, plottable[0].lng],
        14,
        { animate: false },
      );
      return;
    }
    const bounds = L.latLngBounds(plottable.map((p) => [p.lat, p.lng]));
    mapRef.current.fitBounds(bounds, {
      padding: [60, 60],
      maxZoom: 14,
      animate: false,
    });
  }, [plottable.length, plottable]);

  // ── Imperative map controls ─────────────────────────────────────
  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleCycleStyle = () =>
    setStyleId((s) => (s === 'standard' ? 'satellite' : 'standard'));
  const handleLocateMe = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: [number, number] = [
          pos.coords.latitude,
          pos.coords.longitude,
        ];
        setUserPos(coords);
        mapRef.current?.flyTo(coords, 14, { duration: 0.8 });
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000, maximumAge: 60000 },
    );
  };
  const handleFitAll = () => {
    if (plottable.length === 0 || !mapRef.current || !L) return;
    const bounds = L.latLngBounds(plottable.map((p) => [p.lat, p.lng]));
    mapRef.current.fitBounds(bounds, { padding: [40, 40] });
  };

  // ── Native fallback ─────────────────────────────────────────────
  if (Platform.OS !== 'web' || !RL || !L) {
    return (
      <View style={styles.fallback}>
        <MaterialIcons name="map" size={36} color={fpColors.outline} />
        <Text style={styles.fallbackTitle}>Map view is web-only</Text>
        <Text style={styles.fallbackMeta}>
          Open this on app.remembr.xyz to see your items plotted.
        </Text>
      </View>
    );
  }

  const { MapContainer, TileLayer, Marker, Popup, Polyline } = RL;

  const tile = TILE_LAYERS[styleId];

  /** Build the L.divIcon for an inventory item — encapsulated to
   *  ensure the icon refreshes when state changes. */
  const buildItemIcon = (item: AnyItem) => {
    const state = deriveMarkerState(item, currentUserId);
    return L.divIcon({
      className: 'founsi-marker-wrapper',
      html: buildMarkerHTML(item, state),
      iconSize: [MARKER_SIZE, MARKER_SIZE + MARKER_TAIL],
      iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE + MARKER_TAIL],
      popupAnchor: [0, -(MARKER_SIZE + MARKER_TAIL)],
    });
  };

  /** Cluster icon factory — uses the first underlying item's photo
   *  with a count badge bottom-right. */
  const buildClusterIcon = (cluster: any) => {
    const markers = cluster.getAllChildMarkers?.() ?? [];
    const childItems = markers
      .map((m: any) => m.options?.__founsiItem)
      .filter(Boolean);
    return L.divIcon({
      className: 'founsi-marker-wrapper',
      html: buildClusterHTML(childItems),
      iconSize: [MARKER_SIZE, MARKER_SIZE + MARKER_TAIL],
      iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE + MARKER_TAIL],
    });
  };

  /** Blue user-location dot (one-shot). */
  const userDotIcon = L.divIcon({
    className: 'founsi-marker-wrapper',
    html: '<div class="founsi-user-dot"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

  /** Faded ghost pin at the original scan location (Memory Map).
   *  Half-opacity thumbnail with a dashed outline. Tooltip shows
   *  "Originally scanned here". */
  const buildScanGhostIcon = (item: AnyItem) =>
    L.divIcon({
      className: 'founsi-marker-wrapper',
      html: `<div class="founsi-scan-ghost">
        <img src="${escapeAttr(((item as any).primary_photo_url ?? (item as any).source_image_url) || '')}" alt="" loading="lazy" />
      </div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

  return (
    <View style={styles.wrap} testID="location-map-view">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom
        zoomControl={false}
        style={{ width: '100%', height: '100%' }}
        whenCreated={(m: any) => {
          mapRef.current = m;
        }}
      >
        <TileLayer
          key={styleId}
          attribution={tile.attribution}
          url={tile.url}
          maxZoom={tile.maxZoom}
        />

        {MarkerClusterGroup ? (
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={buildClusterIcon}
            showCoverageOnHover={false}
            spiderfyOnMaxZoom
            maxClusterRadius={50}
          >
            {plottable.map(({ item, lat, lng }) => (
              <Marker
                key={(item as any).id}
                position={[lat, lng]}
                icon={buildItemIcon(item)}
                // Pass the item down so the cluster icon factory can
                // pick up its photo when grouping.
                eventHandlers={{}}
                // @ts-ignore — custom option for cluster lookup
                __founsiItem={item}
              >
                <Popup>
                  <PopupContent
                    item={item}
                    onOpen={() => onItemPress(item)}
                  />
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        ) : (
          // Fallback when cluster package failed to load — render
          // markers directly (no clustering).
          plottable.map(({ item, lat, lng }) => (
            <Marker
              key={(item as any).id}
              position={[lat, lng]}
              icon={buildItemIcon(item)}
            >
              <Popup>
                <PopupContent item={item} onOpen={() => onItemPress(item)} />
              </Popup>
            </Marker>
          ))
        )}

        {/* Memory Map — render scan-time ghost pins + dashed polyline
            for items where current_location differs from scan_*. These
            are OUTSIDE the cluster group so they don't fight with
            clustering of primary pins. Tap-pass-through is fine since
            the ghost has no popup of its own; the primary pin owns
            the detail surface. */}
        {plottable.map(({ item, lat, lng, scanGhost }) =>
          scanGhost ? (
            <React.Fragment key={`ghost-${(item as any).id}`}>
              <Marker
                position={[scanGhost.lat, scanGhost.lng]}
                icon={buildScanGhostIcon(item)}
                interactive={false}
              />
              <Polyline
                positions={[
                  [scanGhost.lat, scanGhost.lng],
                  [lat, lng],
                ]}
                pathOptions={{
                  color: fpColors.outline,
                  weight: 2,
                  dashArray: '6 8',
                  opacity: 0.6,
                }}
              />
            </React.Fragment>
          ) : null,
        )}

        {userPos ? <Marker position={userPos} icon={userDotIcon} /> : null}
      </MapContainer>

      <MapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onCycleStyle={handleCycleStyle}
        styleId={styleId}
        onLocateMe={handleLocateMe}
        onFitAll={handleFitAll}
        locating={locating}
      />

      {plottable.length === 0 ? (
        // Apple Photos / Google Photos empty-state pattern: a centered
        // hero card with icon + title + body explaining WHY the map is
        // empty. The map tiles still render behind (so the user
        // understands they're in the right surface), but the card
        // is the focal point. Differentiates the two empty-state
        // causes so the user knows what to do next.
        <View style={styles.emptyCenter} pointerEvents="box-none">
          <View style={styles.emptyCard}>
            <MaterialIcons
              name={items.length === 0 ? 'add-photo-alternate' : 'location-off'}
              size={36}
              color={fpColors.primary}
            />
            <Text style={styles.emptyTitle}>
              {items.length === 0
                ? 'No items yet'
                : 'No location data yet'}
            </Text>
            <Text style={styles.emptyBody}>
              {items.length === 0
                ? 'Scan a room with location services on — items will appear here, plotted where they live.'
                : `${items.length} item${items.length === 1 ? '' : 's'} loaded, but none have GPS coordinates yet. Scan with location on, or edit an item's location to populate the map.`}
            </Text>
          </View>
        </View>
      ) : (
        <View style={styles.countBadge} pointerEvents="none">
          <Text style={styles.countBadgeText}>
            {plottable.length} of {items.length}{' '}
            {items.length === 1 ? 'item' : 'items'} on map
          </Text>
        </View>
      )}
    </View>
  );
};

/**
 * Popup rendered inside Leaflet's bound popup. Uses plain HTML
 * (not RN <Text>) because Leaflet injects this into the DOM
 * outside the RN-web tree.
 */
const PopupContent: React.FC<{
  item: AnyItem;
  onOpen: () => void;
}> = ({ item, onOpen }) => {
  const photo =
    (item as any).primary_photo_url ?? (item as any).source_image_url;
  const scanLat = (item as any).scan_lat;
  const scanLng = (item as any).scan_lng;
  const scanAddress = (item as any).scan_address;
  const scanFallback =
    typeof scanLat === 'number' && typeof scanLng === 'number'
      ? `${scanLat.toFixed(4)}, ${scanLng.toFixed(4)}`
      : null;
  const curLat = (item as any).current_location_lat;
  const curLng = (item as any).current_location_lng;
  const curText = (item as any).current_location_text;
  const curAddress = (item as any).current_location_address;
  const legacyLocation = (item as any).location;
  const currentLabel = curText || legacyLocation;
  const currentFallback =
    typeof curLat === 'number' && typeof curLng === 'number'
      ? `${curLat.toFixed(4)}, ${curLng.toFixed(4)}`
      : null;
  // Structured address fields (BE#143) — when present, render across
  // multiple lines for nicer readability than a flat comma-joined
  // string.
  const curStreet = (item as any).current_location_street;
  const curCity = (item as any).current_location_city;
  const curState = (item as any).current_location_state;
  const curPostcode = (item as any).current_location_postcode;
  const curCountry = (item as any).current_location_country;
  const hasCurStructured = !!(curStreet || curCity || curState || curPostcode || curCountry);
  const scanStreet = (item as any).scan_street;
  const scanCity = (item as any).scan_city;
  const scanState = (item as any).scan_state;
  const scanPostcode = (item as any).scan_postcode;
  const scanCountry = (item as any).scan_country;
  const hasScanStructured = !!(scanStreet || scanCity || scanState || scanPostcode || scanCountry);
  const hasCurrent = !!(curLabel(curText, legacyLocation) || curAddress || currentFallback || hasCurStructured);
  const hasScan = !!(scanAddress || scanFallback || hasScanStructured);
  const showScanLine =
    hasScan
    // suppress "Scanned at" if it's the same address as Current
    && !(curAddress && scanAddress && curAddress === scanAddress);

  return (
    <div style={popupStyles.wrap}>
      {photo ? <img src={photo} alt="" style={popupStyles.photo} /> : null}
      <div style={popupStyles.name}>{item.item_name ?? 'Item'}</div>
      {(item as any).item_category ? (
        <div style={popupStyles.meta}>{(item as any).item_category}</div>
      ) : null}
      {hasCurrent ? (
        <div style={popupStyles.section}>
          <div style={popupStyles.sectionLabel}>📍 Currently:</div>
          {currentLabel ? (
            <div style={popupStyles.sectionValue}>
              <strong>{currentLabel}</strong>
            </div>
          ) : null}
          {hasCurStructured ? (
            <div style={popupStyles.sectionMeta}>
              {curStreet ? <div>{curStreet}</div> : null}
              {(curCity || curState || curPostcode) ? (
                <div>
                  {[curCity, [curState, curPostcode].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              ) : null}
              {curCountry ? <div>{curCountry}</div> : null}
            </div>
          ) : curAddress || currentFallback ? (
            <div style={popupStyles.sectionMeta}>
              {curAddress || currentFallback}
            </div>
          ) : null}
        </div>
      ) : null}
      {showScanLine ? (
        <div style={popupStyles.section}>
          <div style={popupStyles.sectionLabel}>🗂️ Scanned at:</div>
          {hasScanStructured ? (
            <div style={popupStyles.sectionMeta}>
              {scanStreet ? <div>{scanStreet}</div> : null}
              {(scanCity || scanState || scanPostcode) ? (
                <div>
                  {[scanCity, [scanState, scanPostcode].filter(Boolean).join(' ')]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              ) : null}
              {scanCountry ? <div>{scanCountry}</div> : null}
            </div>
          ) : (
            <div style={popupStyles.sectionMeta}>{scanAddress || scanFallback}</div>
          )}
        </div>
      ) : null}
      <button type="button" onClick={onOpen} style={popupStyles.button}>
        Open item
      </button>
    </div>
  );
};

function curLabel(
  curText: string | null | undefined,
  legacy: string | null | undefined,
): string | null {
  return curText || legacy || null;
}

const popupStyles: Record<string, React.CSSProperties> = {
  wrap: {
    minWidth: 200,
    maxWidth: 260,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  photo: {
    width: '100%',
    height: 120,
    // Image-fit sweep (2026-06-23): show the whole pin item photo
    // in the popup; letterbox tone matches the surrounding card.
    objectFit: 'contain',
    backgroundColor: 'var(--md-sys-color-surface-container-low, #f3eef6)',
    borderRadius: 8,
    marginBottom: 8,
  },
  name: {
    fontWeight: 700,
    fontSize: 15,
    color: fpColors.onSurface,
    marginBottom: 2,
  },
  meta: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginBottom: 6,
    textTransform: 'capitalize',
  },
  location: {
    fontSize: 13,
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  address: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginBottom: 10,
  },
  section: {
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 700,
    marginBottom: 2,
  },
  sectionValue: {
    fontSize: 13,
    color: fpColors.onSurface,
  },
  sectionMeta: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    background: fpColors.primary,
    color: fpColors.onPrimary,
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 13,
  },
};

const styles = StyleSheet.create({
  // Full-bleed: fills its parent container. BrowseScreen positions
  // this absolutely so it sits behind the floating top chrome +
  // bottom nav.
  wrap: {
    flex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: fpColors.surfaceContainerLow,
    position: 'relative',
  },
  fallback: {
    paddingVertical: 48,
    alignItems: 'center',
    gap: 8,
  },
  fallbackTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    marginTop: 4,
  },
  fallbackMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  // Centered hero empty state (Apple Photos / Google Photos pattern).
  // Replaces the small bottom banner that was easy to miss. `box-none`
  // pointerEvents on the outer lets pan/zoom pass through to the map.
  emptyCenter: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  emptyCard: {
    alignItems: 'center',
    backgroundColor: fpColors.surface,
    borderRadius: 16,
    paddingVertical: 24,
    paddingHorizontal: 24,
    maxWidth: 380,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 6px 24px rgba(0,0,0,0.15)' } as any)
      : null),
  },
  emptyTitle: {
    marginTop: 12,
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    color: fpColors.onSurface,
    textAlign: 'center',
  },
  emptyBody: {
    marginTop: 6,
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  // Legacy bottom banner — kept for backwards compat if any caller
  // references it; new empty state above supersedes it.
  overlayBanner: {
    position: 'absolute' as any,
    left: 12,
    right: 12,
    bottom: 100, // sit above the floating bottom nav
    alignItems: 'center',
  },
  overlayBannerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: fpColors.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    maxWidth: 420,
  },
  overlayBannerText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurface,
    flex: 1,
  },
  countBadge: {
    position: 'absolute' as any,
    top: 116, // below floating top chrome
    left: 16,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 999,
    zIndex: 5,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } as any)
      : null),
  },
  countBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurface,
  },
});

export default LocationMapView;
